import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const PRODUCT_LINKS = [
  { label: 'Custom Wash Bays', href: 'https://www.kc-qualitysystems.com/all-other-products/wash-bays/' },
  { label: 'Front Load Washers', href: 'https://www.kc-qualitysystems.com/all-other-products/front-load-washers/' },
  { label: 'Conveyor Inline', href: 'https://www.kc-qualitysystems.com/all-other-products/conveyor-inline/' },
  { label: 'Dry Blast Cabinets', href: 'https://www.kc-qualitysystems.com/all-other-products/dry-blast-cabinets/' },
  { label: 'Top Loading Washers', href: 'https://www.kc-qualitysystems.com/all-other-products/top-loading/' },
  { label: 'Ultrasonic Tanks', href: 'https://www.kc-qualitysystems.com/all-other-products/ultrasonic-tanks/' },
  { label: 'Immersion Washers', href: 'https://www.kc-qualitysystems.com/all-other-products/immersion-parts-washers-2/' },
  { label: 'Powder Coat Systems', href: 'https://www.kc-qualitysystems.com/all-other-products/powder-coat-systems/' },
  { label: 'Cure & Burn-Off Ovens', href: 'https://www.kc-qualitysystems.com/all-other-products/gas-electric-cure-ovens/' },
  { label: 'Vapor Degreaser', href: 'https://www.kc-qualitysystems.com/all-other-products/vapor-degreaser/' },
  { label: 'Airless Shot Blasters', href: 'https://www.kc-qualitysystems.com/all-other-products/airless-blast-cabinets/' },
  { label: 'Aerospace', href: 'https://www.kc-qualitysystems.com/all-other-products/aerospace/' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white border-b border-gray-200 shadow-sm'
            : 'bg-white/95 backdrop-blur-sm border-b border-gray-100'
        }`}
      >
        {/* Scroll progress bar */}
        {scrolled && (
          <div className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-gold via-gold-light to-transparent transition-all duration-150"
            style={{ width: `${scrollProgress}%` }}
          />
        )}

        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-8">
          {/* Logo */}
          <a href="https://www.kc-qualitysystems.com/" className="flex-shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/e80394c0-d055-4192-8df1-307f17310fbd/1b41cace-b02f-4c05-b262-bb891ae3fa60_quality-systems.jpg?v=52f93c1aa07df7416dcbd4c6d469cc15"
              alt="KC Quality Systems"
              className="h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7 flex-1 justify-center">
            <Link
              to="/"
              className="text-sm text-gray-600 hover:text-gold transition-colors font-sans tracking-wide whitespace-nowrap"
            >
              Home
            </Link>

            <div ref={dropRef} className="relative">
              <button
                onClick={() => setDropOpen(!dropOpen)}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gold transition-colors font-sans tracking-wide cursor-pointer whitespace-nowrap"
              >
                Products
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${dropOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white border border-gray-100 rounded-lg overflow-hidden shadow-xl shadow-black/10">
                  <div className="py-2">
                    {PRODUCT_LINKS.map((p) => (
                      <a
                        key={p.label}
                        href={p.href}
                        rel="nofollow"
                        className="flex items-center gap-2 px-5 py-2.5 text-sm text-gray-500 hover:text-gold hover:bg-gold/5 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-arrow-right-s-line text-gold/60 text-xs" />
                        {p.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="https://www.kc-qualitysystems.com/about-us/" className="text-sm text-gray-600 hover:text-gold transition-colors font-sans tracking-wide whitespace-nowrap">About</a>
            <a href="https://www.kc-qualitysystems.com/blog-2/" className="text-sm text-gray-600 hover:text-gold transition-colors font-sans tracking-wide whitespace-nowrap">Blog</a>
            <a href="https://www.kc-qualitysystems.com/contact-us/" className="text-sm text-gray-600 hover:text-gold transition-colors font-sans tracking-wide whitespace-nowrap">Contact</a>
          </nav>

          {/* Right CTA — always visible sticky */}
          <div className="hidden lg:flex items-center gap-5 flex-shrink-0">
            <a
              href="tel:8665407575"
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gold transition-colors font-sans whitespace-nowrap tracking-wide"
            >
              <i className="ri-phone-line text-gold" />
              866-540-7575
            </a>
            <div className="h-5 w-px bg-gray-200" />
            <a
              href="#contact"
              className="group px-6 py-2.5 bg-gold text-black text-sm font-bold rounded-md font-sans whitespace-nowrap hover:bg-gold-light transition-all duration-300 cursor-pointer flex items-center gap-2"
            >
              Get a Quote
              <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed top-20 left-0 right-0 z-40 bg-white border-b border-gray-100 shadow-lg px-6 py-6 flex flex-col gap-4 lg:hidden">
          <Link to="/" className="text-gray-600 hover:text-gold transition-colors text-sm font-sans py-1">Home</Link>
          <a href="https://www.kc-qualitysystems.com/about-us/" className="text-gray-600 hover:text-gold transition-colors text-sm font-sans py-1">About</a>
          <a href="https://www.kc-qualitysystems.com/blog-2/" className="text-gray-600 hover:text-gold transition-colors text-sm font-sans py-1">Blog</a>
          <a href="https://www.kc-qualitysystems.com/contact-us/" className="text-gray-600 hover:text-gold transition-colors text-sm font-sans py-1">Contact</a>
          <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
            <a href="tel:8665407575" className="flex items-center gap-2 text-gold font-semibold text-sm font-sans">
              <i className="ri-phone-line" /> 866-540-7575
            </a>
            <a href="#contact" className="px-5 py-3.5 bg-gold text-black text-sm font-bold rounded-md text-center font-sans cursor-pointer whitespace-nowrap">
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </>
  );
}
